import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DocumentsLegacyRedux from '../../components/Documents';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  fetchFaqsNotesDocuments,
  deleteFaqsNotesDocuments,
  deleteOnboardingDocument,
} from 'src/store/actions/faqsNotesDocuments';
import { selectDocuments } from 'src/store/selectors/faqsNotesDocuments';
import { setSearch, clearSearch } from 'src/store/actions/search';
import { fetchSuiteCounts } from 'src/store/actions/suites';
import { selectPRLoader } from 'src/store/selectors/PropertyLoader';
import Loader from '../../components/Loader';
import { sortDocumentsList } from 'src/helpers/CommonHelpers';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import { IContractFile } from '../../../public/APIContracts/APIContracts';
import { ContractFilesRepeater } from 'src/components/ContractFilesRepeater/ContractFilesRepater';
import {
  useDocumentSave,
  UseDocumentSaveReturn,
} from 'src/pages/SharedPageCustomHooks/UseDocumentSave/useDocumentSave';

interface Suite {
  id: string;
  number: string;
  isSpark: boolean;
}

interface SuiteDocumentsProps {
  suite: Suite;
  properties: any;
  contractFiles?: IContractFile[];
}

const SuiteDocuments = ({ suite, contractFiles }: SuiteDocumentsProps) => {
  const user = useSelector(selectAuthenticatedUser);
  const documents = useSelector(selectDocuments);
  const loader = useSelector(selectPRLoader);
  const dispatch = useDispatch();
  const { branding } = useBranding();

  useEffect(() => {
    dispatch(clearSearch());
    dispatch(fetchFaqsNotesDocuments(suite.id, 'SuiteAssets'));
  }, [dispatch, suite.id]);

  const { saveDocument }: UseDocumentSaveReturn = useDocumentSave({
    customErrorMessage: `Failed to upload document to ${branding.text.suite}. Please try again or contact Juniper support if the issue persists.`,
  });

  const onSave = async (document: DocumentFormData): Promise<void> => {
    await saveDocument(document, suite.id, user.id);
  };

  const onDeleteFaqsNotesDocuments = async (
    document: DocumentFormData
  ): Promise<void> => {
    document.Type = 'DOCUMENTS';

    if (document.documentType === 'Onboarding') {
      document.userId = user.id;
      dispatch(deleteOnboardingDocument(document));
      dispatch(fetchSuiteCounts(suite.id, user.roles[0].role.name));
    } else {
      dispatch(deleteFaqsNotesDocuments(document));
      dispatch(fetchSuiteCounts(suite.id, user.roles[0].role.name));
    }
  };

  const sortDocuments = sortDocumentsList(documents);

  return loader ? (
    <Loader />
  ) : (
    <>
      <DocumentsLegacyRedux
        breadcrumb={suite.number}
        documents={sortDocuments}
        onSaveDocument={(document: DocumentFormData) => onSave(document)}
        onDeleteDocument={(document: any) =>
          onDeleteFaqsNotesDocuments(document)
        }
        onSearch={(e: any) =>
          dispatch(setSearch({ documents: e.target.value.trim() }))
        }
        labelName={'Manuals & Documents'}
        from={'Suite'}
        introContent={true}
      />
      {contractFiles && <ContractFilesRepeater contractFiles={contractFiles} />}
    </>
  );
};

export default SuiteDocuments;
